import { useEffect, useMemo, useState } from 'react';
import { AbstractChat, getToolName, isToolUIPart } from 'ai';
import type { ChatState, ChatStatus, FileUIPart } from 'ai';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { createStore } from 'zustand/vanilla';
import type {
  AgentQuestionEvent,
  AgentQuestionItem,
  ComputedFieldDraft,
  DocumentContent,
  ImageContent,
  TaskItem,
} from '../types';
import { toAgentQuestionItem } from '../utils/agentQuestions';
import { type McpMode, transport } from './UseAgentChat/agentChatTransport';
import {
  agentQuestionEventSchema,
  askUserInputSchema,
  chatMessageMetadataSchema,
  computedFieldDraftEventSchema,
  fileCreatedEventSchema,
  taskSnapshotEventSchema,
  tripwireEventSchema,
} from './UseAgentChat/schemas';
import type {
  ChatUIMessage,
  LookupConfiguration,
} from './UseAgentChat/schemas';

// ── Session state (persisted per chatId) ──────────────────────────────

export type ChatSession = {
  messages: ChatUIMessage[];
  outputFiles: Array<{ filename: string; url: string }>;
  status: ChatStatus;
  error: Error | undefined;
  agentQuestion: AgentQuestionEvent | null;
  tasks: TaskItem[];
  lastStreamFinished: number;
  // How the user answered each lookup offer, keyed by the offering message's id. Kept here only:
  // neither answer is sent to the agent, so a reloaded chat shows its offers unanswered.
  lookupDecisions: Record<string, LookupDecision>;
};

const EMPTY_SESSION: ChatSession = {
  messages: [],
  outputFiles: [],
  status: 'ready',
  error: undefined,
  agentQuestion: null,
  tasks: [],
  lastStreamFinished: 0,
  lookupDecisions: {},
};

type SessionUpdater = (prev: ChatSession) => ChatSession;

export const isStreamingStatus = (status: ChatStatus | undefined) =>
  status === 'submitted' || status === 'streaming';

// ── ChatState factory ─────────────────────────────────────────────────

const makeChatState = (
  getSession: () => ChatSession,
  setSession: (updater: SessionUpdater) => void
): ChatState<ChatUIMessage> => ({
  get status() {
    return getSession().status;
  },
  set status(s: ChatStatus) {
    const wasStreaming = isStreamingStatus(getSession().status);
    const isDone = !isStreamingStatus(s);
    setSession(sess => ({
      ...sess,
      status: s,
      lastStreamFinished:
        wasStreaming && isDone ? Date.now() : sess.lastStreamFinished,
    }));
  },
  get error() {
    return getSession().error;
  },
  set error(e: Error | undefined) {
    setSession(s => ({ ...s, error: e }));
  },
  get messages() {
    return getSession().messages;
  },
  set messages(msgs: ChatUIMessage[]) {
    setSession(s => ({ ...s, messages: msgs }));
  },
  pushMessage(msg: ChatUIMessage) {
    setSession(s => ({ ...s, messages: [...s.messages, msg] }));
  },
  popMessage() {
    setSession(s => ({ ...s, messages: s.messages.slice(0, -1) }));
  },
  replaceMessage(index: number, msg: ChatUIMessage) {
    setSession(s => {
      const msgs = [...s.messages];
      msgs[index] = structuredClone(msg);
      return { ...s, messages: msgs };
    });
  },
  snapshot<T>(thing: T): T {
    return structuredClone(thing);
  },
});

// ── AgentChat ─────────────────────────────────────────────────────────

export class AgentChat extends AbstractChat<ChatUIMessage> {
  constructor(
    readonly chatId: string,
    getSession: () => ChatSession,
    private readonly setSession: (updater: SessionUpdater) => void
  ) {
    super({
      transport,
      messageMetadataSchema: chatMessageMetadataSchema,
      state: makeChatState(getSession, setSession),
      onData: dataPart => {
        switch (dataPart.type) {
          case 'data-final-answer':
            break;

          case 'data-agent-question': {
            const parsed = agentQuestionEventSchema.safeParse(dataPart.data);
            if (parsed.success) {
              setSession(s => ({ ...s, agentQuestion: parsed.data }));
              this.stop();
            }
            break;
          }

          case 'data-task-snapshot': {
            const parsed = taskSnapshotEventSchema.safeParse(dataPart.data);
            if (parsed.success) {
              setSession(s => ({ ...s, tasks: parsed.data.tasks }));
            }
            break;
          }

          case 'data-tripwire': {
            // Throwing fails the stream, so the run ends in `error` status with the reason as the
            // error — what the stream would do had the backend sent an `error` chunk.
            const parsed = tripwireEventSchema.safeParse(dataPart.data);
            throw new Error(
              parsed.success ? parsed.data.reason : 'Blocked by processor'
            );
          }

          case 'data-file-created': {
            const parsed = fileCreatedEventSchema.safeParse(dataPart.data);
            if (parsed.success) {
              setSession(s => ({
                ...s,
                outputFiles: [...s.outputFiles, parsed.data],
              }));
            }
            break;
          }

          default:
            break;
        }
      },
    });
  }

  hydrateSession = (
    messages: ChatUIMessage[],
    outputFiles: Array<{ filename: string; url: string }>
  ) => {
    this.setSession(s => ({ ...s, messages, outputFiles }));
  };

  sendAgentMessage = async (
    content: string,
    mcpMode: McpMode,
    attachments?: { images?: ImageContent[]; documents?: DocumentContent[] }
  ) => {
    this.setSession(s => ({
      ...s,
      agentQuestion: s.agentQuestion !== null ? null : s.agentQuestion,
      tasks: s.tasks.length > 0 ? [] : s.tasks,
    }));

    const images = attachments?.images;
    const documents = attachments?.documents;

    // `images` and `imageFiles` are different formats - one accepted by B/E, other accepted by AI SDK store.
    const imageFiles: FileUIPart[] =
      images?.map(img => ({
        type: 'file' as const,
        mediaType: img.media_type,
        url: `data:${img.media_type};base64,${img.data}`,
      })) ?? [];

    await this.sendMessage(
      {
        text: content,
        files: imageFiles,
      },
      { body: { ...this.turnContext(mcpMode), images, documents } }
    );
  };

  /** Resumes the run suspended on an `ask_user` call (see `pendingToolQuestion`) with the answer. */
  answerToolQuestion = (
    toolCallId: string,
    answer: string | string[],
    mcpMode: McpMode
  ) =>
    // No new message: the run picks up where it paused, streaming into the same assistant message.
    this.sendMessage(undefined, {
      body: {
        ...this.turnContext(mcpMode),
        resume: { tool_call_id: toolCallId, data: answer },
      },
    });

  /**
   * Answers a write the agent paused on, then resumes the run: the reply streams into the same
   * assistant message.
   */
  respondToApproval = async (
    approval: PendingApproval,
    approved: boolean,
    mcpMode: McpMode
  ) => {
    await this.addToolApprovalResponse({ id: approval.approvalId, approved });
    // No new message: the transport sends the answered assistant message instead.
    await this.sendMessage(undefined, { body: this.turnContext(mcpMode) });
  };

  private turnContext = (mcpMode: McpMode) => {
    const { fieldDraft } = chatStore.getState();
    return {
      chatId: this.chatId,
      rossum_url: window.location.href,
      mcp_mode: mcpMode,
      // Re-sent every turn because request context is per-request on the backend. This is the
      // snapshot taken when the chat was opened, or the draft the user created since, not the
      // form's current state.
      ...(fieldDraft
        ? { user_data: { computed_field_draft: fieldDraft } }
        : {}),
    };
  };
}

// ── Store ─────────────────────────────────────────────────────────────

type ChatStoreState = {
  // Identifies the chat session shown in the drawer.
  drawerChatId: string | null;
  sessions: Record<string, ChatSession>;
  // Registry of active AgentChat instances keyed by chatId.
  clients: Record<string, AgentChat>;
  // Controls whether the chat drawer is open.
  isOpen: boolean;
  // The computed field the drawer was opened on, snapshotted when the user clicked "Edit all" and
  // sent with every message of that chat. Refreshed only by creating a draft from an offer, not by
  // edits in the form. Cleared when the drawer closes or the user switches chats, so a draft never
  // outlives the conversation it was captured for.
  fieldDraft: ComputedFieldDraft | null;
  // The lookup editor mounted right now, and how to write into its unsaved form values. The offer
  // itself lives in the message list, so nothing here mirrors it.
  lookupTarget: {
    fieldSchemaId: string;
    // Returns the configuration it wrote, which becomes the snapshot sent with later turns.
    apply: (
      result: LookupConfiguration
    ) => Record<string, unknown> & { dataset: string };
  } | null;
  // Every chat that has been shown in the drawer, with the run the user saw there, identified by
  // its `lastStreamFinished` stamp (`undefined` until the first look). A chat whose latest run is
  // a different one has an update the user has not looked at yet (see `hasUnseenUpdate`). Chats
  // missing here never count: the schema editor runs its own chat in the background and applies
  // the result itself. Keyed to the run, not the message count: the assistant message is pushed
  // on the first chunk, so a count would already include a run the user closed the drawer on.
  seenRunEnd: Record<string, number | undefined>;
};

type ChatStoreActions = {
  // Activates a client in the drawer, or clears the active chat when null.
  // Accepting a client (not a raw ID) enforces that a valid AgentChat instance
  // exists for the chat being activated.
  // Also drops any captured field draft: it belongs to the chat being left.
  setDrawerClient: (client: AgentChat | null) => void;
  // Creates and registers a new AgentChat instance for the given chatId.
  createClient: (chatId: string) => AgentChat;
  // Removes the AgentChat instance for the given chatId.
  removeClient: (chatId: string) => void;
  // Updates the active session's state imperatively.
  setDrawerSession: (updater: SessionUpdater) => void;
  // Opens or closes the chat drawer. Closing drops any captured field draft.
  setIsOpen: (open: boolean) => void;
  // Captures the computed field a chat is being opened on. Must be called AFTER
  // setDrawerClient/setIsOpen — both null the draft.
  setFieldDraft: (draft: ComputedFieldDraft | null) => void;
  // Registers the mounted lookup editor as the target for an accepted offer.
  setLookupTarget: (target: ChatStoreState['lookupTarget']) => void;
  // Records that the user has looked at the chat's latest finished run.
  markSeen: (chatId: string) => void;
};

export type ChatStore = ChatStoreState & ChatStoreActions;

export const chatStore = createStore<ChatStore>()((set, get) => ({
  drawerChatId: null,
  sessions: {},
  clients: {},
  isOpen: false,
  fieldDraft: null,
  lookupTarget: null,
  seenRunEnd: {},

  setDrawerClient: client =>
    set(s => ({
      drawerChatId: client?.chatId ?? null,
      fieldDraft: null,
      // Registers the chat as a drawer one; the open drawer marks it seen right after.
      seenRunEnd: client
        ? { ...s.seenRunEnd, [client.chatId]: undefined }
        : s.seenRunEnd,
    })),
  setIsOpen: open =>
    set(open ? { isOpen: true } : { isOpen: false, fieldDraft: null }),
  setFieldDraft: draft => set({ fieldDraft: draft }),

  setLookupTarget: target => set({ lookupTarget: target }),

  markSeen: chatId =>
    set(s => {
      const finished = s.sessions[chatId]?.lastStreamFinished;
      // Returning the state itself keeps subscribers quiet, so marking from one is loop-free.
      if (s.seenRunEnd[chatId] === finished) return s;
      return { seenRunEnd: { ...s.seenRunEnd, [chatId]: finished } };
    }),

  createClient: chatId => {
    const getSession = (): ChatSession =>
      get().sessions[chatId] ?? EMPTY_SESSION;
    const setSession = (updater: SessionUpdater) =>
      set(s => ({
        sessions: {
          ...s.sessions,
          [chatId]: updater(s.sessions[chatId] ?? EMPTY_SESSION),
        },
      }));
    const client = new AgentChat(chatId, getSession, setSession);
    set(s => ({
      clients: { ...s.clients, [chatId]: client },
      sessions: !s.sessions[chatId]
        ? { ...s.sessions, [chatId]: { ...EMPTY_SESSION } }
        : s.sessions,
    }));
    return client;
  },

  removeClient: chatId =>
    set(s => {
      const clients = { ...s.clients };
      delete clients[chatId];
      return { clients };
    }),

  setDrawerSession: updater =>
    set(s => {
      const id = s.drawerChatId;
      if (!id) return {};
      return {
        sessions: {
          ...s.sessions,
          [id]: updater(s.sessions[id] ?? EMPTY_SESSION),
        },
      };
    }),
}));

export const subscribeToStreamSuccess = (
  chatId: string,
  callback: (state: ChatStore) => void
): (() => void) =>
  chatStore.subscribe((state, prevState) => {
    const wasStreaming = isStreamingStatus(prevState.sessions[chatId]?.status);
    const finishedSuccessfully = state.sessions[chatId]?.status === 'ready';
    if (wasStreaming && finishedSuccessfully) callback(state);
  });

/** The chat's latest finished run is not the one the user saw. Nothing counts while one runs. */
export const hasUnseenUpdate = (s: ChatStoreState, chatId: string): boolean => {
  const session = s.sessions[chatId];
  if (!(chatId in s.seenRunEnd) || !session) return false;
  if (isStreamingStatus(session.status)) return false;
  return s.seenRunEnd[chatId] !== session.lastStreamFinished;
};

const selectUnseenChatIds = (s: ChatStore) =>
  Object.keys(s.seenRunEnd).filter(chatId => hasUnseenUpdate(s, chatId));

/** Chats whose latest run ended out of the user's sight. */
export const useUnseenChatIds = (): Set<string> => {
  const unseenIds = useStore(chatStore, useShallow(selectUnseenChatIds));
  return useMemo(() => new Set(unseenIds), [unseenIds]);
};

// The chat shown in the open drawer is seen as it is: on opening, on switching to it, and as its
// run ends in front of the user.
chatStore.subscribe(state => {
  const { isOpen, drawerChatId } = state;
  if (isOpen && drawerChatId) state.markSeen(drawerChatId);
});

// Convenience wrapper for functional-style message updates used by ChatPanel.
export const setMessages = (
  updater: ChatUIMessage[] | ((prev: ChatUIMessage[]) => ChatUIMessage[])
) => {
  chatStore.getState().setDrawerSession(s => ({
    ...s,
    messages: typeof updater === 'function' ? updater(s.messages) : updater,
  }));
};

// ── React hooks ───────────────────────────────────────────────────────

// ── Lookup offers ─────────────────────────────────────────────────────

export type LookupDecision = 'draft' | 'kept';

export type OfferedLookup = {
  result: LookupConfiguration;
  fieldSchemaId: string;
};

/**
 * The configuration this message is offering, read from the conversation rather than mirrored in
 * the store. Newest wins when one turn offered several.
 *
 * Whether it can still be answered is the caller's call: only the conversation's last message can,
 * as a later one has moved past it. How it was answered is in the session's `lookupDecisions`.
 */
export const offeredLookup = (
  message: ChatUIMessage | undefined
): OfferedLookup | null => {
  if (message?.role !== 'assistant') return null;

  const offer = [...message.parts]
    .reverse()
    .flatMap(part =>
      part.type === 'data-computed-field-draft'
        ? [computedFieldDraftEventSchema.safeParse(part.data)]
        : []
    )
    .find(parsed => parsed.success && parsed.data.field_schema_id);

  if (!offer?.success || !offer.data.field_schema_id) return null;
  return {
    result: offer.data.configuration,
    fieldSchemaId: offer.data.field_schema_id,
  };
};

// ── Tool questions ────────────────────────────────────────────────────

export type PendingToolQuestion = {
  toolCallId: string;
  question: AgentQuestionItem;
};

/**
 * The `ask_user` question this message's run is suspended on: the call has no output until the
 * user answers it. Read from the message rather than mirrored in the store, so it survives a
 * reload. The legacy agent's `data-agent-question` is kept in the session instead.
 */
export const pendingToolQuestion = (
  message: ChatUIMessage | undefined
): PendingToolQuestion | null => {
  if (message?.role !== 'assistant') return null;

  const pending = message.parts
    .filter(isToolUIPart)
    .find(
      part =>
        getToolName(part) === 'ask_user' && part.state === 'input-available'
    );
  const input = askUserInputSchema.safeParse(pending?.input);

  if (!pending || !input.success) return null;
  return {
    toolCallId: pending.toolCallId,
    question: toAgentQuestionItem(input.data),
  };
};

// ── Tool approvals ────────────────────────────────────────────────────

export type PendingApproval = {
  approvalId: string;
  toolName: string;
  input: unknown;
};

/**
 * The write the agent paused on for the user's approval, read from the message like an offer. mr-fabry
 * restores the `approval-requested` state when a chat is reloaded, so this holds after a refresh too.
 * The backend runs approval-gated tools one at a time, so there is at most one.
 */
export const pendingApproval = (
  message: ChatUIMessage | undefined
): PendingApproval | null => {
  if (message?.role !== 'assistant') return null;

  const [pending] = message.parts.flatMap(part =>
    isToolUIPart(part) && part.state === 'approval-requested'
      ? [
          {
            approvalId: part.approval.id,
            toolName: getToolName(part),
            input: part.input,
          },
        ]
      : []
  );
  return pending ?? null;
};

/**
 * Answers the offer in the drawer's chat. A draft is written into the editor's unsaved form values,
 * if one is mounted for that field; nothing is saved and nothing reaches the agent.
 */
export const decideOfferedLookup = (
  messageId: string,
  offer: OfferedLookup,
  decision: LookupDecision
): void => {
  const { lookupTarget, fieldDraft, setDrawerSession, setFieldDraft } =
    chatStore.getState();
  if (decision === 'draft') {
    if (offer.fieldSchemaId !== lookupTarget?.fieldSchemaId) return;
    const configuration = lookupTarget.apply(offer.result);
    // The snapshot sent with every turn would otherwise still hold the configuration the draft
    // replaced, and the agent would build its next suggestion on that.
    if (fieldDraft?.field_schema_id === offer.fieldSchemaId) {
      setFieldDraft({
        ...fieldDraft,
        dataset: configuration.dataset,
        configuration,
      });
    }
  }
  setDrawerSession(s => ({
    ...s,
    lookupDecisions: { ...s.lookupDecisions, [messageId]: decision },
  }));
};

export const useChatStore = <T>(selector: (state: ChatStore) => T): T =>
  useStore(chatStore, selector);

export const useChatClient = (chatId: string | null): AgentChat | undefined =>
  useStore(chatStore, s => (chatId ? s.clients[chatId] : undefined));

export type ChatClientState = {
  status: ChatStatus;
  error: Error | undefined;
  agentQuestion: AgentQuestionEvent | null;
  tasks: TaskItem[];
  outputFiles: Array<{ filename: string; url: string }>;
  lastStreamFinished: number;
};

const EMPTY_CLIENT_STATE: ChatClientState = {
  status: 'ready',
  error: undefined,
  agentQuestion: null,
  tasks: [],
  outputFiles: [],
  lastStreamFinished: 0,
};

export const useActiveChatIds = (): Set<string> => {
  const activeIds = useStore(
    chatStore,
    useShallow((s: ChatStore) =>
      Object.keys(s.sessions).filter(chatId =>
        isStreamingStatus(s.sessions[chatId]?.status)
      )
    )
  );
  return useMemo(() => new Set(activeIds), [activeIds]);
};

export const useChatClientState = (chatId: string | null): ChatClientState =>
  useStore(
    chatStore,
    useShallow((s: ChatStore) => {
      if (!chatId) return EMPTY_CLIENT_STATE;
      const session = s.sessions[chatId];
      if (!session) return EMPTY_CLIENT_STATE;
      const {
        status,
        error,
        agentQuestion,
        tasks,
        outputFiles,
        lastStreamFinished,
      } = session;
      return {
        status,
        error,
        agentQuestion,
        tasks,
        outputFiles,
        lastStreamFinished,
      };
    })
  );

const STREAMING_THROTTLE_MS = 50;

const isStreamingStatusForChat = (s: ChatStore, chatId: string | null) =>
  chatId ? isStreamingStatus(s.sessions[chatId]?.status) : false;

// Reads messages for the given chatId but throttles re-renders during streaming
// to at most one per STREAMING_THROTTLE_MS, matching the old experimental_throttle
// behaviour from useChat. The store itself is never throttled — only the React
// render cycle driven by this hook.
export const useChatMessages = (chatId: string | null): ChatUIMessage[] => {
  const [rendered, setRendered] = useState(() =>
    chatId ? (chatStore.getState().sessions[chatId]?.messages ?? []) : []
  );

  useEffect(() => {
    const getMessages = () =>
      chatId ? (chatStore.getState().sessions[chatId]?.messages ?? []) : [];

    // Re-sync immediately when chatId changes.
    setRendered(getMessages());

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const unsubscribe = chatStore.subscribe((state, prevState) => {
      const wasStreaming = isStreamingStatusForChat(prevState, chatId);
      const isStreaming = isStreamingStatusForChat(state, chatId);

      const currentMessages = chatId
        ? (state.sessions[chatId]?.messages ?? [])
        : [];
      const prevMessages = chatId
        ? (prevState.sessions[chatId]?.messages ?? [])
        : [];

      // Flush immediately when streaming ends so the final state is never delayed.
      if (wasStreaming && !isStreaming) {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        setRendered(currentMessages);
        return;
      }

      if (currentMessages === prevMessages) return;

      if (isStreaming) {
        if (timeoutId === null) {
          timeoutId = setTimeout(() => {
            timeoutId = null;
            setRendered(getMessages());
          }, STREAMING_THROTTLE_MS);
        }
      } else {
        setRendered(currentMessages);
      }
    });

    return () => {
      unsubscribe();
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [chatId]);

  return rendered;
};
